// ============================================================================
// BESPOKIAN Pharmacist Cockpit - DUR Review Page
// Drug Utilization Review with alert override workflow
// ============================================================================

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  AlertTriangle,
  AlertCircle,
  Info,
  CheckCircle2,
  ShieldAlert,
  Pill,
} from 'lucide-react';
import { queueApi } from '@/api/queue.api';
import { Button, Badge, Spinner, Alert, Card, CardHeader, CardTitle, CardContent, Textarea } from '@/components/common/ui';
import type { DURAlert } from '@/types';
import toast from 'react-hot-toast';

// Map backend severity values to display config
// Backend uses: INFO, WARNING, CRITICAL
// Frontend types use: LOW, MODERATE, HIGH
const SEVERITY_CONFIG: Record<
  string,
  { icon: typeof AlertCircle; color: string; bg: string; border: string }
> = {
  // Backend values
  CRITICAL: {
    icon: AlertCircle,
    color: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
  },
  WARNING: {
    icon: AlertTriangle,
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
  },
  INFO: {
    icon: Info,
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
  },
  // Frontend type values (for compatibility)
  HIGH: {
    icon: AlertCircle,
    color: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
  },
  MODERATE: {
    icon: AlertTriangle,
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
  },
  LOW: {
    icon: Info,
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
  },
};

// Map alert types to display labels
// Backend types: DRUG_DRUG_INTERACTION, DRUG_ALLERGY, THERAPEUTIC_DUPLICATION, EARLY_REFILL, etc.
const ALERT_TYPE_LABELS: Record<string, string> = {
  // Backend enum values
  DRUG_DRUG_INTERACTION: 'Drug-Drug Interaction',
  DRUG_ALLERGY: 'Drug Allergy',
  THERAPEUTIC_DUPLICATION: 'Duplicate Therapy',
  EARLY_REFILL: 'Early Refill',
  AGE_PRECAUTION: 'Age Precaution',
  DOSE_ALERT: 'Dose Alert',
  PREGNANCY_LACTATION: 'Pregnancy/Lactation',
  CONTROLLED_SUBSTANCE_LIMIT: 'Controlled Substance Limit',
  // Frontend type values (for compatibility)
  DUPLICATE_THERAPY: 'Duplicate Therapy',
  DOSE_TOO_HIGH: 'Dose Too High',
  DOSE_TOO_LOW: 'Dose Too Low',
  PREGNANCY_CONTRAINDICATION: 'Pregnancy Contraindication',
  AGE_CONTRAINDICATION: 'Age Contraindication',
  RENAL_DOSE_ADJUSTMENT: 'Renal Dose Adjustment',
};

export function DURReviewPage() {
  const { id } = useParams({ strict: false });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [overrides, setOverrides] = useState<
    Record<string, { override: boolean; reason: string }>
  >({});

  // Use the pharmacist prescription endpoint which includes DUR alerts
  const { data: rx, isLoading } = useQuery({
    queryKey: ['prescription', id],
    queryFn: () => queueApi.getPrescription(id as string),
    enabled: !!id,
  });

  const submitMutation = useMutation({
    mutationFn: () => queueApi.submitDURReview(id as string, { overrides }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queue'] });
      toast.success('DUR Review Complete');
      navigate({ to: '/queue' });
    },
    onError: (error: Error) => {
      toast.error(`DUR Review Failed: ${error.message}`);
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  const alerts: DURAlert[] = rx?.durAlerts || [];
  const allReviewed = alerts.every(
    (alert: DURAlert) => overrides[alert.id]?.override !== undefined
  );

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate({ to: '/queue' })}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Queue
        </Button>
        <Badge variant="warning">DUR Review Required</Badge>
      </div>

      <div>
        <h1 className="text-2xl font-bold">DUR Review — Rx #{rx?.rxNumber}</h1>
        <p className="text-slate-500">
          {rx?.drugName} for {rx?.patientName}
        </p>
      </div>

      {/* No Alerts */}
      {alerts.length === 0 ? (
        <Alert variant="success" title="No DUR Alerts">
          No drug utilization review alerts were found. You may proceed.
        </Alert>
      ) : (
        <>
          <Alert variant="warning" title={`${alerts.length} DUR Alert(s) Found`}>
            Review each alert and either approve or override with clinical
            justification.
          </Alert>

          {/* Alert Cards */}
          <div className="space-y-4">
            {alerts.map((alert: any) => {
              const config = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.INFO;
              const Icon = config.icon;
              const override = overrides[alert.id];
              
              // Extract interacting drug from alert or details JSON
              const interactingDrug = alert.interactingDrug || alert.details?.interactingDrug;
              const recommendation = alert.recommendation || alert.details?.recommendation;
              const clinicalEffect = alert.clinicalEffect || alert.details?.clinicalEffect;
              
              // Map backend severity to badge variant
              const badgeVariant = 
                alert.severity === 'CRITICAL' || alert.severity === 'HIGH' ? 'danger' :
                alert.severity === 'WARNING' || alert.severity === 'MODERATE' ? 'warning' : 'info';

              return (
                <Card key={alert.id} className={`${config.border} border-l-4`}>
                  <CardHeader>
                    <CardTitle className={`flex items-center gap-2 ${config.color}`}>
                      <Icon className="h-5 w-5" />
                      {ALERT_TYPE_LABELS[alert.type] || alert.type}
                      <Badge variant={badgeVariant}>
                        {alert.severity}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-slate-700">{alert.message}</p>

                    {interactingDrug && (
                      <div className={`${config.bg} p-3 rounded-lg`}>
                        <div className="flex items-center gap-2">
                          <Pill className="h-4 w-4" />
                          <span className="font-medium">Interacting Drug:</span>
                          {interactingDrug}
                        </div>
                        {clinicalEffect && (
                          <p className="text-sm mt-2">{clinicalEffect}</p>
                        )}
                      </div>
                    )}

                    {recommendation && (
                      <div className="text-sm text-slate-600">
                        <strong>Recommendation:</strong> {recommendation}
                      </div>
                    )}

                    {/* Review Actions */}
                    <div className="border-t pt-4">
                      <div className="flex gap-4 mb-3">
                        <Button
                          variant={override?.override === false ? 'danger' : 'outline'}
                          size="sm"
                          onClick={() =>
                            setOverrides({
                              ...overrides,
                              [alert.id]: { override: false, reason: '' },
                            })
                          }
                        >
                          <AlertCircle className="h-4 w-4 mr-1" />
                          Do Not Dispense
                        </Button>
                        <Button
                          variant={override?.override === true ? 'success' : 'outline'}
                          size="sm"
                          onClick={() =>
                            setOverrides({
                              ...overrides,
                              [alert.id]: {
                                override: true,
                                reason: override?.reason || '',
                              },
                            })
                          }
                        >
                          <ShieldAlert className="h-4 w-4 mr-1" />
                          Override & Continue
                        </Button>
                      </div>

                      {override?.override === true && (
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Override Reason (Required)
                          </label>
                          <Textarea
                            placeholder="Enter clinical justification for override..."
                            value={override.reason}
                            onChange={(e) =>
                              setOverrides({
                                ...overrides,
                                [alert.id]: { override: true, reason: e.target.value },
                              })
                            }
                            rows={2}
                          />
                        </div>
                      )}

                      {override?.override === false && (
                        <Alert variant="danger" className="mt-2">
                          This prescription will be placed on hold and cannot be
                          filled.
                        </Alert>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {/* Submit */}
      <div className="flex justify-end pt-6 border-t">
        <Button
          disabled={alerts.length > 0 && !allReviewed}
          onClick={() => submitMutation.mutate()}
          loading={submitMutation.isPending}
        >
          <CheckCircle2 className="h-4 w-4 mr-2" />
          {alerts.length === 0 ? 'Continue to Fill' : 'Submit DUR Review'}
        </Button>
      </div>
    </div>
  );
}
